/**
 * Shared constants across the application
 */

// Model families ordered by tier (cheapest → most capable).
// Single source of truth for the frontend; mirrors backend ALLOWED_MODEL_FAMILIES.
export const MODEL_FAMILIES = [
  { key: "haiku", label: "Haiku", shortLabel: "H", color: "#10b981" },
  { key: "sonnet", label: "Sonnet", shortLabel: "S", color: "#6366f1" },
  { key: "opus", label: "Opus", shortLabel: "O", color: "#f59e0b" },
];

// Credit exhaustion behavior options for widget configuration.
// Matches backend's credit_exhaustion_behavior JSONB column on widgets table.
export const CREDIT_EXHAUSTION_OPTIONS = [
  { value: "block", label: "Block responses" },
  { value: "downgrade", label: "Auto-downgrade to cheaper model" },
];

// Default exhaustion behavior — cheapest family gets "block" (no cheaper tier to fall
// back to); all others get "downgrade". Derived from MODEL_FAMILIES so adding a new
// family here is automatically reflected.
// Mirrors the derivation logic in backend/utils/creditHelpers.js — sanitizeExhaustionBehavior().
// ONE external source must still stay in sync manually:
//   Migration 20260212300000 — .defaultTo(JSON.stringify({...})) on the widgets table.
// If you add or reorder model families, update the migration's defaultTo as well.
const [cheapestFamily, ...pricierFamilies] = MODEL_FAMILIES;
export const DEFAULT_CREDIT_EXHAUSTION_BEHAVIOR = {
  [cheapestFamily.key]: "block",
  ...Object.fromEntries(pricierFamilies.map((f) => [f.key, "downgrade"])),
};

// Knowledge source type icons
export const KNOWLEDGE_SOURCE_TYPE_ICONS = {
  file: "📄",
  url: "🔗",
  text: "📝",
};

// Knowledge source status configuration
export const KNOWLEDGE_SOURCE_STATUS = {
  processing: {
    label: "Processing",
    className: "status-processing",
    color: "#856404",
    bgColor: "#fff3cd",
  },
  completed: {
    label: "Ready",
    className: "status-completed",
    color: "#155724",
    bgColor: "#d4edda",
  },
  failed: {
    label: "Failed",
    className: "status-failed",
    color: "#721c24",
    bgColor: "#f8d7da",
  },
};

// Default widget configuration
export const DEFAULT_WIDGET_CONFIG = {
  // Rate limit per IP (messages per hour — mirrors backend WIDGET_LIMITS)
  RATE_LIMIT_PER_IP_MIN: 1,
  RATE_LIMIT_PER_IP_MAX: 1000,
  RATE_LIMIT_PER_IP_DEFAULT: 20,

  // AI behavior
  TEMPERATURE_MIN: 0,
  TEMPERATURE_MAX: 1,
  TEMPERATURE_DEFAULT: 0.7,

  MAX_TOKENS_MIN: 100,
  MAX_TOKENS_MAX: 4096,
  MAX_TOKENS_DEFAULT: 1024, // ~750 words

  // Conversation settings
  CONVERSATION_TTL_MIN: 1,
  CONVERSATION_TTL_MAX: 720, // 30 days
  CONVERSATION_TTL_DEFAULT: 168, // 7 days

  // Field length limits
  NAME_MAX_LENGTH: 100,
  DISPLAY_NAME_MAX_LENGTH: 100,
  SYSTEM_PROMPT_MIN_LENGTH: 10,
  SYSTEM_PROMPT_MAX_LENGTH: 5000,

  // Branding customization defaults (must match migration defaults)
  PRIMARY_COLOR_DEFAULT: "#007bff",
  SECONDARY_COLOR_DEFAULT: "#6c757d",
  BRANDING_TEXT_DEFAULT: "Powered by Kwibl",
};

// File upload configuration
export const FILE_UPLOAD_CONFIG = {
  MAX_SIZE_BYTES: 10 * 1024 * 1024, // 10MB
  ALLOWED_EXTENSIONS: [".pdf", ".txt", ".doc", ".docx", ".md"],
  ALLOWED_MIME_TYPES: [
    "application/pdf",
    "text/plain",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/markdown",
  ],
};

// UI Constants
export const UI_CONSTANTS = {
  HEX_COLOR_MAX_LENGTH: 7, // #RRGGBB format
  CONVERSATION_PREVIEW_MAX_LENGTH: 100, // Max characters to show in conversation preview
  CONVERSATION_ID_DISPLAY_LENGTH: 6, // Last N chars of conversation ID to show
  SESSION_ID_DISPLAY_LENGTH: 8, // Last N chars of session ID to show
  TEMPERATURE_STEP: 0.1, // Slider step for temperature adjustment
};

// Form validation messages
export const VALIDATION_MESSAGES = {
  CLEAR_DRAFT: "Clear saved draft? This cannot be undone.",
  DRAFT_RESTORED: "Draft restored from",
  DRAFT_EXPIRED: "Your draft has expired and was cleared.",
  FILE_REQUIRED: "Please select a file",
  FILE_TOO_LARGE: "File too large. Maximum size is",
  FILE_INVALID_EXTENSION: "File must have a valid extension",
  FILE_INVALID_TYPE: "Invalid file type",
  URL_REQUIRED: "Please enter a URL",
  URL_INVALID: "Invalid URL. Please use http:// or https:// URLs only.",
  TEXT_REQUIRED: "Please enter both title and content",
  WIDGET_NAME_REQUIRED: "Widget name is required",
  WIDGET_NAME_TOO_LONG: `Widget name must be ${DEFAULT_WIDGET_CONFIG.NAME_MAX_LENGTH} characters or less`,
  DISPLAY_NAME_TOO_LONG: `Display name must be ${DEFAULT_WIDGET_CONFIG.DISPLAY_NAME_MAX_LENGTH} characters or less`,
  COLOR_INVALID: "Invalid color format. Use hex format like #007bff",
  WELCOME_MESSAGE_REQUIRED: "Welcome message is required",
  PLACEHOLDER_REQUIRED: "Placeholder text is required",
  URL_FORMAT_INVALID: "Please enter a valid HTTP or HTTPS URL",
  DISPLAY_NAME_REQUIRED: "Display name is required",
  SYSTEM_PROMPT_REQUIRED: "System prompt is required",
  SYSTEM_PROMPT_TOO_SHORT: `System prompt must be at least ${DEFAULT_WIDGET_CONFIG.SYSTEM_PROMPT_MIN_LENGTH} characters`,
  TEMPERATURE_RANGE: `Temperature must be between ${DEFAULT_WIDGET_CONFIG.TEMPERATURE_MIN} and ${DEFAULT_WIDGET_CONFIG.TEMPERATURE_MAX}`,
  MAX_TOKENS_RANGE: `Max tokens must be between ${DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MIN} and ${DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MAX}`,
  RATE_LIMIT_RANGE: `Rate limit must be between ${DEFAULT_WIDGET_CONFIG.RATE_LIMIT_PER_IP_MIN} and ${DEFAULT_WIDGET_CONFIG.RATE_LIMIT_PER_IP_MAX}`,
  CONVERSATION_TTL_RANGE: `Conversation TTL must be between ${DEFAULT_WIDGET_CONFIG.CONVERSATION_TTL_MIN} and ${DEFAULT_WIDGET_CONFIG.CONVERSATION_TTL_MAX} hours`,
  INVALID_DOMAINS: "Invalid domain(s):",
};

// Plan invite statuses (mirrors backend/constants/invites.js)
export const INVITE_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  EXPIRED: "expired",
  REVOKED: "revoked",
};

// Draft storage configuration
export const DRAFT_CONFIG = {
  STORAGE_KEY: "widget_form_draft",
  EXPIRY_DAYS: 7,
  MAX_AGE_MS: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
};

/**
 * Branding fields that are restricted by plan (frontend field names).
 *
 * CROSS-REFERENCE: This is the frontend counterpart of backend's
 * BRANDING_CUSTOMIZATION_FIELDS in backend/config/widgetSchema.js which uses
 * DB column names (e.g., "bot_avatar_url"). This list uses frontend names
 * (e.g., "avatar_url"). The mapping between the two is defined in
 * WIDGET_FIELD_MAPPINGS below.
 *
 * Keep in sync: when adding a new branding field, update BOTH this array
 * AND the backend's BRANDING_CUSTOMIZATION_FIELDS in widgetSchema.js.
 */
export const BRANDING_RESTRICTED_FRONTEND_FIELDS = [
  "primary_color",
  "secondary_color",
  "avatar_url", // Backend: bot_avatar_url (see WIDGET_FIELD_MAPPINGS)
  "welcome_message",
  "placeholder_text",
  "branding_text",
  "branding_url",
  "show_branding",
];

// Field name mappings between frontend form and backend API.
//   avatar_url            → bot_avatar_url          (bot avatar vs future user avatar)
//   persist_conversations → conversation_persistence (backend column name)
// Add new mappings to _frontendToApi only — apiToFrontend is derived automatically.
const _frontendToApi = {
  avatar_url: "bot_avatar_url",
  persist_conversations: "conversation_persistence",
};

export const WIDGET_FIELD_MAPPINGS = {
  // Maps frontend form field name → backend API field name
  frontendToApi: _frontendToApi,
  // Derived inverse — add new mappings to frontendToApi only
  apiToFrontend: Object.fromEntries(
    Object.entries(_frontendToApi).map(([k, v]) => [v, k]),
  ),
};
